import { apiFetch, type QueryParams } from './http';

/**
 * Reads the filename out of a Content-Disposition header.
 *
 * RFC 5987's `filename*` is tried first: when both forms are present the plain `filename` is the
 * ASCII-degraded fallback the server offers to clients that cannot decode the extended one.
 */
export function parseContentDispositionFilename(header: string | null | undefined): string | null {
  if (!header) return null;

  const extended = /filename\*\s*=\s*([^;]+)/i.exec(header);
  if (extended?.[1]) {
    const value = extended[1].trim().replace(/^["']|["']$/g, '');
    const parts = value.split("'");
    // charset'language'percent-encoded-value — the value may itself contain apostrophes, so only
    // the first two separators are structural.
    const encoded = parts.length >= 3 ? parts.slice(2).join("'") : value;
    try {
      const decoded = decodeURIComponent(encoded);
      if (decoded.length > 0) return sanitizeFileName(decoded);
    } catch {
      // A malformed percent-encoding is not worth failing a download over; fall through to `filename`.
    }
  }

  const plain = /filename\s*=\s*("([^"]*)"|[^;]*)/i.exec(header);
  const raw = plain?.[2] ?? plain?.[1];
  if (raw) {
    const trimmed = raw.trim();
    if (trimmed.length > 0) return sanitizeFileName(trimmed);
  }

  return null;
}

/** Strips any path the header may carry, so a download cannot suggest a directory to write into. */
function sanitizeFileName(name: string): string {
  const last = name.split(/[/\\]/).pop();
  return last && last.length > 0 ? last : name;
}

export interface DownloadOptions {
  query?: QueryParams;
  /** Used when the response carries no Content-Disposition filename. */
  fallbackFileName?: string;
}

/**
 * Downloads an API file. Every endpoint needs the bearer header, which a plain `<a href>` cannot
 * send, so the bytes are fetched, wrapped in an object URL and handed to a synthetic anchor.
 */
export async function downloadApiFile(path: string, options: DownloadOptions = {}): Promise<void> {
  const response = await apiFetch(path, {
    accept: 'application/octet-stream',
    ...(options.query ? { query: options.query } : {}),
  });

  const fileName =
    parseContentDispositionFilename(response.headers.get('content-disposition')) ??
    options.fallbackFileName ??
    'download';

  const blob = await response.blob();
  saveBlob(blob, fileName);
}

export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Revoking synchronously can cancel the download in some browsers; one turn of the event loop
    // is enough for the click to have been taken up.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
